// The session gate's pure pieces — moved out of auth.js so an entry
// that does not load the whole auth module (the comm app,
// comm/functions.js) registers the SAME predicate the workspace shell's
// router guard and cold boot run. auth.js re-exports every one of them.
import _wsShared from '@symbo-ls/workspace-shared'

// Detects a live local session — the Symbols SDK token family is the only
// session signal (the per-project sb-* Supabase tokens died with that plane).
export const hasSessionToken = () => {
  if (typeof window === 'undefined') return false
  try {
    const sym =
      window.localStorage.getItem('symbols_access_token') ||
      // bootShell accepts the bridge token as proof of a session too, so this
      // must agree with it or the two disagree mid-boot.
      window.localStorage.getItem('symbols_bridge_access_token')
    if (sym) {
      const exp = +window.localStorage.getItem('symbols_expires_at')
      if (!exp || exp > Date.now()) return true
      // An EXPIRED access token alongside a live refresh token is a valid
      // session, not a signed-out one: the SDK's TokenManager reports exactly
      // this state as 'valid' ("expired but recoverable") and rotates it on
      // boot with delay 0. Calling it signed-out here bounced anyone returning
      // after an idle hour to /login despite a recoverable session.
      if (window.localStorage.getItem('symbols_refresh_token')) return true
    }
  } catch (e) {
    /* localStorage may throw in some sandbox contexts */
  }
  return false
}

// Full guest-only route set. A signed-in user landing on ANY of these
// (sign-in, sign-up, password recovery, email verification) gets
// bounced to `homePath` so auth chrome never renders for an already-
// authenticated session.
export const AUTH_PATHS = [
  '/login',
  '/signin',
  '/signup',
  '/signup-confirmation',
  '/reset-password',
  '/forgot-password',
  '/verify-email',
  // demo.symbols.app email gate — a guest-only surface like the sign-in
  // pages: chrome stays hidden (app.js derives its route-kind regex from
  // this list) and an authed visitor on /demo bounces home.
  '/demo'
]

// THE AUTH PREDICATE — the one decision both boots make for a visitor with no
// session: the parcel cold boot (redirectIfUnauthed, below, before the bundle
// mounts anything) and the router guard (functions/authRouteGuard.js, every
// navigation, before mount — the published shell's only gate, since mermaid
// never runs index.js). Returns where to send the visitor, or null to let the
// path render. Pure: no storage, no navigation.
//
// `isPublicPath` is THE public-path list the boot gate reads too — shared
// guestRoutes.js isGuestAllowedPath (one-boot review D7). `publicPathPrefixes`
// stays for callers that add their own.
export const authRedirectTarget = ({
  path,
  search = '',
  authed,
  demoHost = false,
  loginPath = '/login',
  authPaths = AUTH_PATHS,
  publicPathPrefixes = [],
  isPublicPath = null
} = {}) => {
  const p = String(path || '/')
  if (publicPathPrefixes.some((x) => p === x || p.startsWith(x + '/') || p.startsWith(x + '?')))
    return null
  if (typeof isPublicPath === 'function' && isPublicPath(p)) return null
  if (authed) {
    // CORE-AUTH-TAKEOVER-PATHS D9 — a sign-in link from a CLI or plugin
    // (`/login?session=<id>`, or `/?session=<id>` once the visitor signed in
    // on it) goes to the explicit Continue step: the server no longer arms
    // the session as a side effect, so the bounce home would strand the
    // device. Only `/` and the auth pages carry a sign-in `session`.
    if (p === '/' || authPaths.some((ap) => p === ap || p.startsWith(ap + '/'))) {
      const sid = new URLSearchParams(search || '').get('session')
      if (sid && sid.length <= 200) return `/sign-in-session?session=${encodeURIComponent(sid)}`
    }
    return null
  }
  for (const ap of authPaths) {
    if (p === ap || p.startsWith(ap + '/') || p.startsWith(ap + '?')) return null
  }
  // On a demo host (demo.symbols.app / dev.demo.symbols.app, or the local
  // ?demo=1 / symbols_force_demo override) unauthed visitors go to the
  // branded /demo email gate instead of the real /login. /demo is in
  // AUTH_PATHS, so once they land there this returns null — no loop.
  if (demoHost) return '/demo'
  // Preserve the requested destination (path + query) through the bounce
  // via `?next=` — the same convention bootShell's _forceLogoutAndReauth
  // and the SPA nav-guard already use, and the `authed && onAuthPage`
  // branch below already honors on the way back. Dropping it stranded
  // every inbound deep link, invitation ?token= links above all (P0
  // invite-flow fix, 2026-08-06). Bare `/` stays a plain /login.
  // LOGINLOOP-1: build `dest` from the current path plus its query MINUS
  // any `next` the URL already carries. Appending `search` verbatim (what
  // this did before) re-wrapped an existing `next` instead of reusing it,
  // so every bounce nested one level deeper —
  // `/?next=%2F` -> `/login?next=%2F%3Fnext%3D%252F` -> … byte-for-byte
  // the reported loop URL. The `dest !== '/'` guard below could not catch
  // it either: once `search` held a `next`, `dest` was never bare `/`.
  // Only when the current URL carries nothing of its own (bare `/`) is the
  // carried `next` the real destination — resolveAuthNext unwraps it
  // repeatedly so a hand-nested URL collapses to its innermost target
  // instead of being re-wrapped. Preferring `selfDest` first is what keeps
  // an invitation deep link (`/accept-org-invite?token=…&next=%2F`) intact:
  // resolveAuthNext alone would return the carried `/` and drop the token,
  // undoing the P0 invite-flow fix this block exists for.
  const _params = new URLSearchParams(search || '')
  _params.delete('next')
  const _query = _params.toString()
  const selfDest = p + (_query ? `?${_query}` : '')
  const dest = selfDest === '/' ? _wsShared.functions.resolveAuthNext(p, search || '') : selfDest
  const next = dest && dest !== '/' ? `?next=${encodeURIComponent(dest)}` : ''
  return `${loginPath}${next}`
}

// Pages the shell renders WITHOUT its chrome (header, zoom navbar, composer):
// every guest-only AUTH_PATHS entry, the public share viewer (/s/<id>), and
// the four invitation landings. The landings are NOT guest-only — a signed-in
// member accepts an invitation there too — so they join this rule and never
// AUTH_PATHS (live on the white-label landing, vault.pr 2026-10-11: the
// accept card rendered inside the workspace header with the Symbols mark).
// Self-contained (FA204): the guest list is restated, and kept equal to
// AUTH_PATHS by __tests__/shellChromelessInviteLanding.test.js.
export const isChromelessShellPath = (path) => {
  const p = typeof path === 'string' && path ? path : '/'
  const names = [
    'login',
    'signin',
    'signup',
    'signup-confirmation',
    'reset-password',
    'forgot-password',
    'verify-email',
    'demo',
    'accept-invite',
    'accept-org-invite',
    'accept-team-invite',
    'accept-workspace-invite',
    's/[^/]+'
  ]
  return new RegExp('^/(' + names.join('|') + ')(/|$)').test(p)
}
